import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import shadows from '@material-ui/core/styles/shadows';

const CATEGORY_BASE_COLORS = [
    { name: 'healthBlue', baseColorHex: '#2E739B' },
    { name: 'conflictRed', baseColorHex: '#B03939' },
    { name: 'outbreakGreen', baseColorHex: '#446E33' },
    { name: 'ebolaRed', baseColorHex: '#7f0a34' },
    { name: 'politicalOrange', baseColorHex: '#BA5A15' },
    { name: 'vaccinationGold', baseColorHex: '#B19701' },
    { name: 'maternalPurple', baseColorHex: '#6F428B' },
    { name: 'droneBrown', baseColorHex: '#703131' },
];

const hexToRgb = (hex: string) => {
    const cleanedHex = hex.replace('#', '');
    const bigint = Number.parseInt(cleanedHex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
};

const rgbToHex = (r: number, g: number, b: number) => {
    return (
        '#' +
        [r, g, b]
            .map(x => x.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase()
    );
};

const generateIntermediateColors = (baseColorHex: string) => {
    const interpolationFactors = [0.25, 0.5, 0.75];

    const white = hexToRgb('#FFFFFF');
    const baseColor = hexToRgb(baseColorHex);

    const colors = interpolationFactors.map(t => {
        const r = Math.round(white.r + (baseColor.r - white.r) * t);
        const g = Math.round(white.g + (baseColor.g - white.g) * t);
        const b = Math.round(white.b + (baseColor.b - white.b) * t);
        return rgbToHex(r, g, b);
    });
    return colors;
};

const generateCategoryColorPalette = (
    categoryColors: Array<{ name: string; baseColorHex: string }>
) => {
    const palette: Record<string, string> = {};

    for (const { name, baseColorHex } of categoryColors) {
        const [color25, color50, color75] = generateIntermediateColors(
            baseColorHex
        );

        palette[name] = baseColorHex; // Base color (100%)
        palette[`${name}75`] = color75; // 75% opacity
        palette[`${name}50`] = color50; // 50% opacity
        palette[`${name}25`] = color25; // 25% opacity
    }

    return palette;
};

const categoryColorPalette = generateCategoryColorPalette(CATEGORY_BASE_COLORS);

export const defaultTheme = responsiveFontSizes(
    createMuiTheme({
        spacing: 8,
        typography: {
            fontFamily: 'Montserrat',
            h3: {
                fontWeight: 500,
            },
        },
        palette: {
            text: {
                primary: '#323031', // default text color
                secondary: '#323031', // secondary text color
            },
            primary: {
                main: '#f8f8f8',
            },
            secondary: {
                main: '#323031',
            },
            colors: {
                white: '#f8f8f8',
                yellow: '#ffdb58',
                grey100: '#dcdcdc',
                grey200: '#2f4f4f',
                ...categoryColorPalette,
            },
        },
        overrides: {
            MuiTypography: {
                h6: { fontWeight: 100 },
            },
            MuiCardActionArea: {
                root: {
                    '&:hover .MuiCardActionArea-focusHighlight': {
                        opacity: 0.12,
                    },
                },
            },
            MuiButton: {
                contained: {
                    'boxShadow': shadows[1],
                    '&:hover': { boxShadow: shadows[1] },
                    '&:active': { boxShadow: shadows[1] },
                },
            },
        },
    })
);
